import type { ReactNode } from 'react';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type { CatalogItem } from '../../../wire';
import { headingMap, priceText, textOf } from './text';

/**
 * The left half: everything of one kind, grouped under its headings, one row
 * each — name, lengths or minutes, price, and the flags worth seeing before
 * opening anything ("not on sale").
 *
 * Groups keep the order the catalog read lists items in (the owner's own
 * position numbers), so the list reads the way the site does. Items under no
 * heading come last, under a label that says so rather than a blank.
 *
 * **No "length not confirmed" flag.** The mockup draws one for durations
 * somebody guessed during onboarding. Those guesses are marked only in the
 * catalog file's comments (the spike file's `_` notes), which the seeder does
 * not store, so the catalog read has nothing to draw it from — and a flag the
 * console invented would be a second, drifting list of which lengths are
 * guesses.
 */
export function ItemList({
  items,
  locale,
  currency,
  selected,
  onSelect,
}: {
  items: readonly CatalogItem[];
  locale: string;
  currency: string;
  selected: string | null;
  onSelect: (itemId: string) => void;
}): ReactNode {
  const t = useT();
  const uiLocale = useLocale();
  const groups = groupByHeading(items, locale, t('catalog.list.noHeading'));

  return (
    <div className="catalog-list">
      {groups.map((group) => (
        <section key={group.heading} aria-label={group.heading}>
          <h5 className="catalog-list__heading">{group.heading}</h5>
          <ul className="catalog-list__items">
            {group.items.map((item) => (
              <li key={item.item_id}>
                <button
                  type="button"
                  className="catalog-row"
                  aria-current={item.item_id === selected ? 'true' : undefined}
                  data-item={item.item_id}
                  onClick={() => onSelect(item.item_id)}
                >
                  <span className="catalog-row__main">
                    <span className="catalog-row__name">
                      {textOf(item.name, locale) || item.item_id}
                    </span>
                    <span className="catalog-row__meta">{metaOf(t, item).join(' · ')}</span>
                  </span>
                  <span className="catalog-row__price">
                    {listPrice(t, item, currency, uiLocale)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function groupByHeading(
  items: readonly CatalogItem[],
  locale: string,
  none: string,
): { heading: string; items: CatalogItem[] }[] {
  const order: string[] = [];
  const byHeading = new Map<string, CatalogItem[]>();
  let unfiled: CatalogItem[] = [];
  for (const item of items) {
    const heading = textOf(headingMap(item), locale).trim();
    if (heading === '') {
      unfiled = [...unfiled, item];
      continue;
    }
    if (!byHeading.has(heading)) {
      order.push(heading);
      byHeading.set(heading, []);
    }
    byHeading.get(heading)?.push(item);
  }
  const groups = order.map((heading) => ({ heading, items: byHeading.get(heading) ?? [] }));
  return unfiled.length === 0 ? groups : [...groups, { heading: none, items: unfiled }];
}

/** The line under the name: how long, or who makes it, then the flags. */
export function metaOf(t: T, item: CatalogItem): string[] {
  const facts: string[] = [];
  const lengths = item.options
    .filter((o) => o.active && o.duration_min !== null)
    .map((o) => o.duration_min as number);
  if (lengths.length > 0) {
    facts.push(t('catalog.list.lengths', { lengths: lengths.join(' · ') }));
  } else if (item.scheduling !== null) {
    facts.push(t('catalog.list.minutes', { minutes: item.scheduling.duration_min }));
  } else if (item.departure !== null) {
    facts.push(t('catalog.list.minutes', { minutes: item.departure.duration_min }));
  } else if (item.stay !== null) {
    facts.push(t('catalog.list.sleeps', { n: item.stay.max_occupancy }));
  }
  if (item.retail !== null && item.retail.brand.trim() !== '') facts.push(item.retail.brand);
  if (item.scheduling?.attach_only === true) facts.push(t('catalog.list.attachOnly'));
  if (item.menu?.sold_out === true) facts.push(t('catalog.list.soldOut'));
  if (!item.active) facts.push(t('catalog.list.notOnSale'));
  return facts;
}

/** "$85", or "from $85" when the ways it is sold cost different amounts. */
function listPrice(t: T, item: CatalogItem, currency: string, uiLocale: string): string {
  if (item.open_price) return t('catalog.list.openPrice');
  if (item.stay !== null) {
    return t('catalog.list.from', {
      price: priceText(item.stay.base_nightly_cents, currency, uiLocale),
    });
  }
  const prices = item.options.filter((o) => o.active).map((o) => o.price_cents);
  if (new Set(prices).size > 1) {
    return t('catalog.list.from', {
      price: priceText(Math.min(...prices), currency, uiLocale),
    });
  }
  return priceText(item.price_cents, currency, uiLocale);
}
